// UTM Promo Code activation service
import { settingsService, PromoCode } from './settingsService';
import { analytics } from '../utils/analytics';

class UTMPromoService {
    private static instance: UTMPromoService;
    private activePromo: PromoCode | null = null;
    private listeners: ((promo: PromoCode | null) => void)[] = [];

    constructor() {
        this.initializeHashListener();
        this.checkInitialHash();
    }

    static getInstance(): UTMPromoService {
        if (!UTMPromoService.instance) {
            UTMPromoService.instance = new UTMPromoService();
        }
        return UTMPromoService.instance;
    }

    private initializeHashListener(): void {
        window.addEventListener('hashchange', () => {
            this.checkHashForPromoCode();
        }, { passive: true });
    }

    private checkInitialHash(): void {
        this.checkHashForPromoCode();
    }

    private async checkHashForPromoCode(): Promise<void> {
        const hash = window.location.hash.replace('#', '');

        if (!hash) {
            this.setActivePromo(null);
            return;
        }

        try {
            const promoCode = await settingsService.activateUTMPromoCode(hash);

            if (promoCode) {
                this.setActivePromo(promoCode);
                this.showPromoBanner(promoCode);                analytics.track('utm_promo_code_activated', 'utm_promo_service', {
                    code: promoCode.code,
                    utmTrigger: hash,
                    discount: promoCode.discountValue,
                    discountType: promoCode.discountType,
                });
            } else {
                // Hash doesn't match any UTM promo code
                this.setActivePromo(null);
            }
        } catch (error) {
            console.error('Error checking hash for promo code:', error);
            analytics.trackError('utm_promo_check_failed', `Hash: ${hash}, Error: ${error instanceof Error ? error.message : 'Unknown'}`);
            this.setActivePromo(null);
        }
    }

    private setActivePromo(promo: PromoCode | null): void {
        this.activePromo = promo;
        this.notifyListeners(promo);
    }    private showPromoBanner(promo: PromoCode): void {
        // Create and show promotional banner
        const banner = document.createElement('div');
        banner.id = 'utm-promo-banner';
        banner.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 9999;
      background: ${promo.bannerColor || '#28a745'};
      color: white;
      padding: 12px;
      text-align: center;
      font-weight: bold;
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      animation: slideDown 0.3s ease-out;
    `;

        // Add CSS animation
        if (!document.querySelector('#utm-promo-banner-styles')) {
            const style = document.createElement('style');
            style.id = 'utm-promo-banner-styles';
            style.textContent = `
        @keyframes slideDown {
          from { transform: translateY(-100%); }
          to { transform: translateY(0); }
        }
        @keyframes slideUp {
          from { transform: translateY(0); }
          to { transform: translateY(-100%); }
        }
        .utm-banner-exit {
          animation: slideUp 0.3s ease-out forwards;
        }
      `;
            document.head.appendChild(style);
        }        const bannerText = promo.bannerText ||
            `🎉 ${promo.code} activated! Get ${promo.discountValue}${promo.discountType === 'percentage' ? '%' : '$'} off your service!`;

        banner.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center; max-width: 1200px; margin: 0 auto;">
        <div style="flex: 1;">${bannerText}</div>
        <button onclick="this.parentElement.parentElement.remove()" 
                style="background: rgba(255,255,255,0.2); border: none; color: white; padding: 4px 8px; border-radius: 4px; cursor: pointer; margin-left: 16px;">
          ×
        </button>
      </div>
    `;

        // Remove existing banner if present
        const existingBanner = document.getElementById('utm-promo-banner');
        if (existingBanner) {
            existingBanner.remove();
        }

        document.body.appendChild(banner);

        // Auto-remove banner after 10 seconds
        setTimeout(() => {
            if (banner.parentElement) {
                banner.classList.add('utm-banner-exit');
                setTimeout(() => banner.remove(), 300);
            }
        }, 10000);
    }

    public getActivePromo(): PromoCode | null {
        return this.activePromo;
    }

    public onPromoChange(callback: (promo: PromoCode | null) => void): () => void {
        this.listeners.push(callback);

        // Return unsubscribe function
        return () => {
            const index = this.listeners.indexOf(callback);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }

    private notifyListeners(promo: PromoCode | null): void {
        this.listeners.forEach(callback => callback(promo));
    }

    public clearActivePromo(): void {
        // Remove hash from URL
        if (window.location.hash) {
            window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }

        this.setActivePromo(null);

        // Remove banner if present
        const banner = document.getElementById('utm-promo-banner');
        if (banner) {
            banner.classList.add('utm-banner-exit');
            setTimeout(() => banner.remove(), 300);
        }
    }

    public applyPromoToBooking(bookingTotal: number): { discountAmount: number; finalTotal: number } {
        if (!this.activePromo) {
            return { discountAmount: 0, finalTotal: bookingTotal };
        }

        let discountAmount = 0;        if (this.activePromo.discountType === 'percentage') {
            discountAmount = (bookingTotal * this.activePromo.discountValue) / 100;
        } else {
            discountAmount = this.activePromo.discountValue;
        }

        // Ensure discount doesn't exceed total
        discountAmount = Math.min(discountAmount, bookingTotal);

        const finalTotal = Math.max(0, bookingTotal - discountAmount);        analytics.track('promo_discount_applied', 'utm_promo_service', {
            code: this.activePromo.code,
            originalTotal: bookingTotal,
            discountAmount,
            finalTotal,
            discountType: this.activePromo.discountType,
        });

        return { discountAmount, finalTotal };
    }
}

// Export singleton instance
export const utmPromoService = UTMPromoService.getInstance();
export default utmPromoService;
